/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useMutation } from '@apollo/client';
import { AddIcon, AttachmentIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Container,
	Divider,
	Flex,
	FormControl,
	IconButton,
	ListItem,
	Popover,
	PopoverArrow,
	PopoverBody,
	PopoverCloseButton,
	PopoverContent,
	PopoverHeader,
	PopoverTrigger,
	Switch,
	Text,
	Tooltip,
	UnorderedList,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import { faEdit, faPrint, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import JoditEditor from 'jodit-react';
import { useEffect, useMemo, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';
import DownloadAndViewButton from '../../../../components/Files/DownloadAndViewButton';
import RichTextEditorViewer from '../../../../components/form/RichTextEditorField/viewer';
import LabelDetail from '../../../../components/LabelDetail';
import Tip from '../../../../components/Tip/Tip';
import c from '../../../../constant';
import useStore from '../../../../store';
import checkOneRole from '../../../../utils/checkOneRole';
import jodit from '../../../../utils/jodit';
import { canEditNp } from '../../../../utils/np-permission';
import AdditionalAttachment from '../additional-attachment/additional-attachment';
import AddOtherAttachmentModal from './AddOtherAttachmentModal';
import AddReference from './AddReferenceModal';
import EditReference from './EditReferenceModal';

const SAVE_EDIT_NP = gql`
	mutation saveEditNp($id: String!, $subject: String!, $description: String!, $references: JSON, $attachments: JSON, $attachings: JSON) {
		saveEditNp(id: $id, subject: $subject, description: $description, references: $references, attachments: $attachments, attachings: $attachings) {
			id
			references
		}
	}
`;

const M_SAVE_SIGNED_PDF = gql`
	mutation saveSignedPdf($id: String!, $attachmentId: String!, $tempSignedPdf: String!) {
		saveSignedPdf(id: $id, attachmentId: $attachmentId, tempSignedPdf: $tempSignedPdf) {
			id
		}
	}
`;

const M_DELETE_SIGNED_PDF = gql`
	mutation deleteSignedPdf($id: String!, $attachmentId: String!) {
		deleteSignedPdf(id: $id, attachmentId: $attachmentId) {
			id
		}
	}
`;

const M_OTHER_ATTACHMENTS = gql`
	mutation allowAddingOtherAttachments($id: String!, $status: Boolean!) {
		allowAddingOtherAttachments(id: $id, status: $status) {
			id
			createdBy
			createdAt
			step
			number
			subject
			references
			description
			attachments
			attachings
			proposalNumber
			doneFile
			allowAddingOtherAttachments
			allowAddingOtherAttachmentsBy
			type
			office
			department
			completedAt
			urgent
			isPrinted
			createdByUser {
				id
				username
				fullName
				fullNameKhmer
				avatar
				position
				office
				department
			}
			viewers {
				id
				createdAt
				createdBy
				userViewer {
					id
					username
					fullName
					fullNameKhmer
					avatar
					department
					office
				}
				isAssistantAssignment
			}
			timelines {
				id
				createdAt
				user {
					id
					username
					fullName
					fullNameKhmer
				}
				action
				description
			}
			proposal {
				id
				no
			}
			checkInAt
			checkOutAt
			checkInDdgAt
			checkInDgAt
			assistantColor
			isDepartmentLevel
			relation {
				id
				np {
					id
					number
					subject
					step
				}
				offices
				departments
				relations {
					id
					number
					subject
					step
				}
				createdAt
				updatedAt
				deleted
			}
			taggings {
				id
				npId
				tag {
					id
					createdAt
					createdBy
					name
				}
			}
		}
	}
`;

function NPEditor({ detail = null, onChange = () => null, isEditing = false, setEditing = () => null }) {
	const toast = useToast();
	const referenceModal = useDisclosure();
	const attachingModal = useDisclosure();
	const otherModal = useDisclosure();
	const cancelEditingModal = useDisclosure();
	const { t } = useTranslation();
	const { currentUser } = useStore((state) => state?.currentUser);
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const [subject, setSubject] = useState('');
	const [references, setReferences] = useState([]);
	const [description, setDescription] = useState('');
	const [removingReferenceId, setRemovingReferenceId] = useState(null);
	const [removingAttachingId, setRemovingAttachingId] = useState(null);
	const [saveEditNP, { data: dataSaveEditNp, loading: loadingSaveEditNp, error: saveEditError }] = useMutation(SAVE_EDIT_NP);
	const [additionalAttachmentData, setAdditionalAttachmentData] = useState(null);
	const [attachings, setAttachings] = useState([]);
	const [editReference, setEditReference] = useState(null);
	const [editAttaching, setEditAttaching] = useState(null);
	const [canAddingOtherAttachments, setCanAddingOtherAttachments] = useState(false);
	const [allowAddingOtherAttachments, { loading: loadingAllowAddingOtherAttachments }] = useMutation(M_OTHER_ATTACHMENTS);
	const [saveSignedPdfMutation] = useMutation(M_SAVE_SIGNED_PDF);
	const [deleteSignedPdfMutation] = useMutation(M_DELETE_SIGNED_PDF);

	// Shared with the AI panel's "Save corrections", which writes through the
	// same saveEditNp mutation and so must obey the same rule.
	const canEdit = useMemo(() => canEditNp(detail, currentUser), [detail, currentUser]);

	useEffect(() => {
		if (detail) {
			setSubject(detail?.subject || '');
			setReferences(detail?.references || []);
			setDescription(detail?.description || '');
			setAdditionalAttachmentData(detail?.attachments || []);
			setAttachings(detail?.attachings || []);
			setCanAddingOtherAttachments(detail?.allowAddingOtherAttachments);
		}
	}, [detail]);

	useEffect(() => {
		if (dataSaveEditNp?.saveEditNp) {
			setEditing(false);
			onChange();
		}
	}, [dataSaveEditNp]);

	useEffect(() => {
		if (saveEditError) {
			if (saveEditError?.graphQLErrors?.length > 0) {
				saveEditError?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(saveEditError?.message ? saveEditError?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [saveEditError]);

	function saveSignedPdf(attachmentId, tempSignedPdf) {
		return saveSignedPdfMutation({ variables: { id: detail?.id, attachmentId, tempSignedPdf } }).then(() => {
			onChange();
		});
	}

	function deleteSignedPdf(attachmentId) {
		return deleteSignedPdfMutation({ variables: { id: detail?.id, attachmentId } }).then(() => {
			onChange();
		});
	}

	function saveEdit(attachments, attachings, id, subject, references, description) {
		saveEditNP({
			variables: {
				id,
				subject,
				references,
				description,
				attachments,
				attachings,
			},
		});
	}

	const sortedReferences = useMemo(() => {
		return [...references].sort((a, b) => (a?.no || 0) - (b?.no || 0));
	}, [references]);

	return (
		<>
			<Flex justifyContent={{ base: 'center', sm: 'center', md: 'flex-end' }}>
				<Button
					mr="2"
					onClick={() => window?.print()}
					leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
					colorScheme="gray"
					variant="solid"
					size="sm"
					borderRadius={c.borderRadius}
					border="1px solid #bdc3c7"
					w="100px"
				>
					{t('Print')}
				</Button>
				{isEditing ? (
					<>
						<Button
							mr="2"
							leftIcon={<FontAwesomeIcon icon={faTimes} style={{ fontSize: 16 }} />}
							colorScheme="red"
							variant="solid"
							size="sm"
							borderRadius={c.borderRadius}
							border="1px solid #bdc3c7"
							w="100px"
							onClick={cancelEditingModal?.onOpen}
						>
							{t('Cancel')}
						</Button>
						<Button
							isLoading={loadingSaveEditNp}
							mr="2"
							leftIcon={<FontAwesomeIcon icon={faSave} style={{ fontSize: 16 }} />}
							colorScheme="green"
							variant="solid"
							size="sm"
							borderRadius={c.borderRadius}
							border="1px solid #bdc3c7"
							w="100px"
							onClick={() => saveEdit(additionalAttachmentData, attachings, detail?.id, subject, references, description)}
						>
							{t('Save')}
						</Button>
					</>
				) : (
					<Button
						mr="2"
						leftIcon={<FontAwesomeIcon icon={faEdit} style={{ fontSize: 16 }} />}
						colorScheme="gray"
						variant="solid"
						size="sm"
						borderRadius={c.borderRadius}
						border="1px solid #bdc3c7"
						onClick={() => setEditing(true)}
						isDisabled={canEdit ? false : true}
						w="100px"
					>
						{t('Edit')}
					</Button>
				)}
			</Flex>
			<Divider mt="4" />
			{isEditing ? (
				<>
					<Box mt="4">
						<Box key={10000} width="100%" mb={8}>
							<Text fontSize="lg" mb="2" fontWeight="bold">
								{t('Subject')}
							</Text>
							<div key={10001}>
								<JoditEditor value={subject} config={editorConfigSubject} onChange={setSubject} />
							</div>
						</Box>
						<Box key={20000} width="100%" mb={8}>
							<Text fontSize="lg" mb="2" fontWeight="bold">
								{t('Reference')}
							</Text>
							{sortedReferences?.map((item, i) => (
								<Flex flex={1} key={i} justifyContent="flex-start" alignItems="center">
									<UnorderedList width="100%" onClick={() => setEditReference(item)}>
										<ListItem cursor="pointer" _hover={{ bg: 'gray.100' }} textAlign={'justify'}>
											<Flex flex={1}>
												<Flex flex={1}>
													<Flex flex={1} mr={4} justifyContent="flex-start">
														<Text fontSize="md" fontWeight="normal">
															{ReactHtmlParser(item?.description)}
														</Text>
													</Flex>
													<Flex justifyContent="flex-end" minWidth={12}>
														{(item?.file?.url || item?.file?.id) && <AttachmentIcon color="blue.500" mt={1} />}
													</Flex>
												</Flex>
											</Flex>
										</ListItem>
									</UnorderedList>
									<Tooltip label={t('Edit')}>
										<IconButton
											size="sm"
											colorScheme="blue"
											icon={<EditIcon />}
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											onMouseDown={() => setEditReference(item)}
											ml={4}
											my={1}
										/>
									</Tooltip>
									<Tooltip label={t('Remove')}>
										<IconButton
											size="sm"
											colorScheme="red"
											icon={<DeleteIcon />}
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											onMouseDown={() => setRemovingReferenceId(item?.id)}
											ml={2}
											my={1}
										/>
									</Tooltip>
								</Flex>
							))}
							<Tooltip label={t('Add')}>
								<IconButton
									size="sm"
									colorScheme="blue"
									icon={<AddIcon />}
									borderRadius={c.borderRadius}
									border="1px solid #bdc3c7"
									onMouseDown={referenceModal?.onOpen}
								/>
							</Tooltip>
						</Box>
						<Box key={30000} width="100%" mb={8}>
							<Text fontSize="lg" mb="2" fontWeight="bold">
								{t('Attachments')}
							</Text>
							{attachings?.map((item, i) => (
								<Flex flex={1} key={i} justifyContent="flex-start" alignItems="center">
									<UnorderedList width="100%" onClick={() => setEditAttaching(item)}>
										<ListItem cursor="pointer" _hover={{ bg: 'gray.100' }} textAlign={'justify'}>
											<Flex flex={1}>
												<Flex flex={1}>
													<Flex flex={1} mr={4} justifyContent="flex-start">
														<Text fontSize="md" fontWeight="normal">
															{ReactHtmlParser(item?.description)}
														</Text>
													</Flex>
													<Flex justifyContent="flex-end" minWidth={12}>
														{(item?.file?.url || item?.file?.id) && <AttachmentIcon color="blue.500" mt={1} />}
													</Flex>
												</Flex>
											</Flex>
										</ListItem>
									</UnorderedList>
									<Tooltip label={t('Edit')}>
										<IconButton
											size="sm"
											colorScheme="blue"
											icon={<EditIcon />}
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											onMouseDown={() => setEditAttaching(item)}
											ml={4}
											my={1}
										/>
									</Tooltip>
									<Tooltip label={t('Remove')}>
										<IconButton
											size="sm"
											colorScheme="red"
											icon={<DeleteIcon />}
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											onMouseDown={() => setRemovingAttachingId(item?.id)}
											ml={2}
											my={1}
										/>
									</Tooltip>
								</Flex>
							))}
							<Tooltip label={t('Add')}>
								<IconButton
									size="sm"
									colorScheme="blue"
									icon={<AddIcon />}
									borderRadius={c.borderRadius}
									border="1px solid #bdc3c7"
									onMouseDown={attachingModal?.onOpen}
								/>
							</Tooltip>
						</Box>
						<Divider />
						<Box key={40000} width="100%" mb={8} mt={4}>
							<Text fontSize="lg" mb="2" fontWeight="bold">
								{t('Description')}
							</Text>
							<div key={40001}>
								<JoditEditor value={description} config={editorConfigDetail} onBlur={setDescription} />
							</div>
						</Box>
						<Divider />
						<Box key={50000} width="100%" mb={8}>
							<AdditionalAttachment
								isEditing={isEditing}
								initData={additionalAttachmentData}
								onChange={(e) => {
									setAdditionalAttachmentData(e);
								}}
							/>
						</Box>
					</Box>
				</>
			) : (
				<Container maxW="container.xl" mt="16px" mb="16px">
					<Box mt="16px">
						<Box w="100%" overflow="hidden" _hover={{ bg: 'gray.100' }} mb={8}>
							<Text fontSize="lg" mb="2" fontWeight="bold">
								{t('Subject')}
							</Text>
							<Box w="100%">
								<RichTextEditorViewer value={detail?.subject} />
							</Box>
						</Box>
						{sortedReferences?.length > 0 && (
							<Box w="100%" mb={8}>
								<Text fontSize="lg" mb="2" fontWeight="bold">
									{t('Reference')}
								</Text>
								{sortedReferences?.map((item, i) => {
									return (
										<UnorderedList key={i}>
											<ListItem _hover={{ bg: 'gray.100' }}>
												<Flex flex={1} justifyContent="flex-start" alignItems="center">
													<Flex flex={1} mr={4} justifyContent="flex-start">
														<RichTextEditorViewer value={item?.description} />
													</Flex>
													<Flex justifyContent="flex-end" minWidth={12}>
														{item?.file?.url && (
															<Popover>
																<PopoverTrigger>
																	<AttachmentIcon cursor="pointer" color="blue.500" mt={1} />
																</PopoverTrigger>
																<PopoverContent>
																	<PopoverArrow />
																	<PopoverCloseButton />
																	<PopoverHeader>{t('Attachment')}</PopoverHeader>
																	<PopoverBody>
																		<Flex justify={'center'}>
																			<DownloadAndViewButton url={item?.file?.url} size={item?.file?.size} />
																		</Flex>
																	</PopoverBody>
																</PopoverContent>
															</Popover>
														)}
													</Flex>
												</Flex>
											</ListItem>
										</UnorderedList>
									);
								})}
							</Box>
						)}
						{attachings?.length > 0 && (
							<Box w="100%" mb={8}>
								<Text fontSize="lg" mb="2" fontWeight="bold">
									{t('Attachments')}
								</Text>
								{attachings.map((item, i) => (
									<UnorderedList key={i}>
										<ListItem _hover={{ bg: 'gray.100' }}>
											<Flex flex={1} justifyContent="flex-start" alignItems="center">
												<Flex flex={1} mr={4} justifyContent="flex-start">
													<RichTextEditorViewer value={item?.description} />
												</Flex>
												<Flex justifyContent="flex-end" minWidth={12}>
													{item?.file?.url && (
														<Popover>
															<PopoverTrigger>
																<AttachmentIcon cursor="pointer" color="blue.500" mt={1} />
															</PopoverTrigger>
															<PopoverContent>
																<PopoverArrow />
																<PopoverCloseButton />
																<PopoverHeader>{t('Attachment')}</PopoverHeader>
																<PopoverBody>
																	<Flex justify={'center'}>
																		<DownloadAndViewButton url={item?.file?.url} size={item?.file?.size} />
																	</Flex>
																</PopoverBody>
															</PopoverContent>
														</Popover>
													)}
												</Flex>
											</Flex>
										</ListItem>
									</UnorderedList>
								))}
							</Box>
						)}
						{detail?.description && (
							<>
								<Divider />
								<Box w="100%" mb={8} mt={4}>
									<RichTextEditorViewer value={detail?.description} />
								</Box>
								<Divider />
							</>
						)}
						<Box width="100%">
							<AdditionalAttachment
								isEditing={isEditing}
								initData={additionalAttachmentData}
								onChange={(e) => {
									setAdditionalAttachmentData(e);
								}}
								onSaveSignedPdf={saveSignedPdf}
								canViewSignedPdf={checkOneRole(currentUser?.role, 'admin') || checkOneRole(currentUser?.role, 'assistant')}
								canSignPdf={checkOneRole(currentUser?.role, 'admin')}
								canPreviewPdf={
									!checkOneRole(currentUser?.role, 'admin')
									// && !checkOneRole(currentUser?.role, 'assistant')
								}
								canDeleteSignedPdf={checkOneRole(currentUser?.role, 'admin')}
								onDeleteSignedPdf={deleteSignedPdf}
							/>
						</Box>
						{(currentUser?.role === 'admin' || checkOneRole(currentUser?.role, 'assistant') || currentUser?.position === '1') && (
							<Box width="100%" mt="4">
								<LabelDetail
									mb={1}
									mt={2}
									labelWidth={lang === 'kh' ? '230px' : '186px'}
									direction="row"
									label="Allow edit other attachments"
									guide={
										<Tip
											childDescription={
												<Text fontSize="sm" fontWeight="400" color="blue.500">
													<UnorderedList styleType="none" ml={0} spacing={3}>
														<ListItem lineHeight="tall">
															<Text as="span" fontWeight="bold" color="blue.600">
																• មុខងារ៖
															</Text>{' '}
															អនុញ្ញាតឱ្យកែសម្រួលឯកសារភ្ជាប់ផ្សេងៗ (អាចអនុវត្តបាននៅគ្រប់ដំណាក់កាលនៃកំណត់បង្ហាញ ឬរបាយការណ៍)។
														</ListItem>

														<ListItem lineHeight="tall">
															<Text as="span" fontWeight="bold" color="blue.600">
																• សិទ្ធិអនុញ្ញាត៖
															</Text>{' '}
															ផ្តល់ជូនតែគណនីប្រភេទ «អគ្គនាយក» និង «ជំនួយការ» តែប៉ុណ្ណោះ។
														</ListItem>
													</UnorderedList>
												</Text>
											}
										/>
									}
									childDetail={
										<FormControl display="flex" justifyContent="flex-start" alignItems="center" mt={1} ml={2}>
											<Switch
												isReadOnly={loadingAllowAddingOtherAttachments}
												isChecked={canAddingOtherAttachments}
												id="other-attachments"
												colorScheme="blue"
												size="md"
												onChange={(e) => {
													const id = detail?.id;
													const status = canAddingOtherAttachments ? false : true;
													const variables = { id, status };
													setCanAddingOtherAttachments(status);
													allowAddingOtherAttachments({ variables })
														.then(({ data }) => {
															onChange();
														})
														.catch((error) => {
															if (error?.graphQLErrors?.length > 0) {
																error?.graphQLErrors.map(({ message }, i) =>
																	toast({
																		title: t(message),
																		status: 'error',
																		isClosable: true,
																		position: 'bottom-right',
																	}),
																);
															} else {
																toast({
																	title: t(error?.message ? error?.message : 'something went wrong'),
																	status: 'error',
																	isClosable: true,
																	position: 'bottom-right',
																});
															}
														});
												}}
											/>
										</FormControl>
									}
								/>
							</Box>
						)}
						{canAddingOtherAttachments && (
							<Box width="100%" mt="2">
								<Button onMouseDown={otherModal?.onOpen} colorScheme="blue" variant="outline" size="sm">
									{t('Edit other attachments')}
								</Button>
							</Box>
						)}
					</Box>
				</Container>
			)}
			{referenceModal?.isOpen && (
				<AddReference
					title="Reference"
					list={references}
					onClose={referenceModal?.onClose}
					onApply={(list) => {
						setReferences(list);
						referenceModal?.onClose();
					}}
				/>
			)}
			{editReference && (
				<EditReference
					title="Reference"
					item={editReference}
					onClose={() => setEditReference(null)}
					onApply={(reference) => {
						setReferences((prev) => prev.map((ref) => (ref?.id === reference?.id ? reference : ref)));
						setEditReference(null);
					}}
				/>
			)}
			{attachingModal?.isOpen && (
				<AddReference
					title="Attachments"
					list={attachings}
					onClose={attachingModal?.onClose}
					onApply={(list) => {
						setAttachings(list);
						attachingModal?.onClose();
					}}
				/>
			)}
			{editAttaching && (
				<EditReference
					title="Attachments"
					item={editAttaching}
					onClose={() => setEditAttaching(null)}
					onApply={(attaching) => {
						setAttachings((prev) => prev.map((att) => (att?.id === attaching?.id ? attaching : att)));
						setEditAttaching(null);
					}}
				/>
			)}
			{otherModal?.isOpen && (
				<AddOtherAttachmentModal npId={detail?.id} initData={detail?.attachments || []} onClose={otherModal?.onClose} onCallback={onChange} />
			)}
			<AlertDialog isOpen={cancelEditingModal?.isOpen} onClose={cancelEditingModal?.onClose}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Cancel')}
						</AlertDialogHeader>
						<AlertDialogBody fontSize="md">
							{detail?.type === 'np' && t('Are you sure you want to cancel editing this note of presentation?')}
							{detail?.type === 'rp' && t('Are you sure you want to cancel editing this report?')}
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button mr={4} onClick={cancelEditingModal?.onClose}>
								{t('No')}
							</Button>
							<Button
								colorScheme="red"
								onClick={() => {
									cancelEditingModal?.onClose();
									setEditing(false);
									onChange();
								}}
							>
								{t('Yes')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
			<AlertDialog isOpen={removingReferenceId ? true : false} onClose={() => setRemovingReferenceId(null)}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Remove')}
						</AlertDialogHeader>
						<AlertDialogBody fontSize="16px">{t('Are you sure to remove reference?')}</AlertDialogBody>
						<AlertDialogFooter>
							<Button mr={4} onClick={() => setRemovingReferenceId(null)}>
								{t('No')}
							</Button>
							<Button
								colorScheme="red"
								onClick={() => {
									setRemovingReferenceId(null);
									setReferences((prev) => prev.filter((ref) => ref?.id !== removingReferenceId));
								}}
							>
								{t('Yes')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
			<AlertDialog isOpen={removingAttachingId ? true : false} onClose={() => setRemovingAttachingId(null)}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Remove')}
						</AlertDialogHeader>
						<AlertDialogBody>{t('Are you sure to remove attachment?')}</AlertDialogBody>
						<AlertDialogFooter>
							<Button mr={4} onClick={() => setRemovingAttachingId(null)}>
								{t('No')}
							</Button>
							<Button
								colorScheme="red"
								onClick={() => {
									setRemovingAttachingId(null);
									setAttachings((prev) => prev.filter((att) => att?.id !== removingAttachingId));
								}}
							>
								{t('Yes')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
}

export default NPEditor;

const editorConfigSubject = {
	minHeight: 100,
	showPlaceholder: false,
	toolbarSticky: false,
	toolbarAdaptive: false,
	toolbarInlineForSelection: false,
	inline: false,
	readonly: false,
	toolbar: true,
	statusbar: false,
	buttons: jodit?.buttons,
	controls: jodit?.controls,
};

const editorConfigDetail = {
	minHeight: 250,
	showPlaceholder: false,
	toolbarSticky: false,
	toolbarAdaptive: false,
	toolbarInlineForSelection: false,
	inline: false,
	readonly: false,
	toolbar: true,
	statusbar: false,
	buttons: jodit?.buttons,
	controls: jodit?.controls,
};
